import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Search, Edit, Trash2, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import type { CarOfDara } from './index';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export default function CarsOfDaraListPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [cars, setCars] = useState<CarOfDara[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [plateStatusFilter, setPlateStatusFilter] = useState<string>('all');
  const [carOfFilter, setCarOfFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchCars = async () => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearchTerm.trim()) params.append('search', debouncedSearchTerm.trim());
      if (plateStatusFilter && plateStatusFilter !== 'all') params.append('plateStatus', plateStatusFilter);
      if (carOfFilter.trim()) params.append('carOf', carOfFilter.trim());
      params.append('page', String(currentPage));
      params.append('limit', String(itemsPerPage));

      const res = (await api.get(`/api/cars-of-dara?${params.toString()}`)) as {
        cars?: CarOfDara[];
        pagination?: PaginationData;
      };
      setCars(res?.cars ?? []);
      setPagination(res?.pagination ?? null);
    } catch (e) {
      console.error(e);
      setCars([]);
      setPagination(null);
    } finally {
      setTableLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, [debouncedSearchTerm, plateStatusFilter, carOfFilter, currentPage]);

  const clearFilters = () => {
    setSearchTerm('');
    setPlateStatusFilter('all');
    setCarOfFilter('');
    setCurrentPage(1);
  };

  const formatPlateStatus = (status: string | null) => {
    if (!status) return '-';
    if (status === 'TEMPORARY') return t('carsOfDara.plateStatusTemporary');
    if (status === 'PERMANENT') return t('carsOfDara.plateStatusPermanent');
    return status;
  };

  const goToNextPage = () => { if (pagination?.hasNextPage) setCurrentPage((p) => p + 1); };
  const goToPrevPage = () => { if (pagination?.hasPrevPage) setCurrentPage((p) => p - 1); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearchTerm.trim()) params.append('search', debouncedSearchTerm.trim());
      if (plateStatusFilter && plateStatusFilter !== 'all') params.append('plateStatus', plateStatusFilter);
      if (carOfFilter.trim()) params.append('carOf', carOfFilter.trim());

      const blob = await api.getBlob(`/api/cars-of-dara/export${params.toString() ? `?${params.toString()}` : ''}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cars-of-dara.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('carsOfDara.exportSuccess'));
    } catch (e) {
      console.error(e);
      toast.error(t('carsOfDara.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (carId: number) => {
    try {
      await api.delete(`/api/cars-of-dara/${carId}`);
      toast.success(t('carsOfDara.carDeleted'));
      fetchCars();
    } catch (e) {
      console.error(e);
      toast.error(t('carsOfDara.carAddError'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('sellCar.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild className="border-slate-200">
              <Link to="/cars-of-dara">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('carsOfDara.addCar')}
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
                {t('carsOfDara.listTitle')}
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {pagination ? `${pagination.totalCount} ${pagination.totalCount === 1 ? t('carsOfDara.car') : t('carsOfDara.cars')}` : '...'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">{t('carsOfDara.listTitle')}</CardTitle>
                <CardDescription>{t('carsOfDara.subtitle')}</CardDescription>
              </div>
              <Badge variant="secondary" className="bg-slate-100 text-slate-800 w-fit">
                {pagination?.totalCount ?? 0} {((pagination?.totalCount ?? 0) === 1 ? t('carsOfDara.car') : t('carsOfDara.cars'))}
              </Badge>
            </div>

            <div className="mt-6">
              <div className="flex flex-col lg:flex-row gap-4 mb-4">
                <div className="flex-1 lg:max-w-md">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">{t('exchangeCarHistory.search')}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder={t('carsOfDara.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full rounded-lg border-slate-200"
                    />
                  </div>
                  {searchTerm && (
                    <p className="text-xs text-slate-500 mt-1">{t('exchangeCarHistory.searchingFor')}: "{searchTerm}"</p>
                  )}
                </div>

                <div className="w-full lg:w-48">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">{t('carsOfDara.plateStatus')}</Label>
                  <Select value={plateStatusFilter} onValueChange={setPlateStatusFilter}>
                    <SelectTrigger className="border-slate-200">
                      <SelectValue placeholder={t('carsOfDara.plateStatus')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">—</SelectItem>
                      <SelectItem value="TEMPORARY">{t('carsOfDara.plateStatusTemporary')}</SelectItem>
                      <SelectItem value="PERMANENT">{t('carsOfDara.plateStatusPermanent')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-full lg:w-48">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">{t('carsOfDara.carOf')}</Label>
                  <Input
                    value={carOfFilter}
                    onChange={(e) => setCarOfFilter(e.target.value)}
                    placeholder="Dara"
                    className="border-slate-200"
                  />
                </div>

                <div className="flex items-end gap-2">
                  <Button onClick={clearFilters} variant="outline" className="w-full md:w-auto border-slate-200 text-slate-700 hover:bg-slate-50">
                    {t('exchangeCarHistory.clearFilters')}
                  </Button>
                  <Button
                    onClick={handleExport}
                    disabled={exporting || (pagination?.totalCount ?? 0) === 0}
                    variant="outline"
                    className="w-full md:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {exporting ? t('carsOfDara.exporting') : t('carsOfDara.exportToExcel')}
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {cars.length === 0 && !tableLoading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-12 text-center">
                <p className="text-slate-500 text-lg">{t('carsOfDara.noCars')}</p>
                <p className="text-sm text-slate-500 mt-2">
                  {(searchTerm || plateStatusFilter !== 'all' || carOfFilter)
                    ? t('exchangeCarHistory.tryAdjusting')
                    : t('carsOfDara.noCars')}
                </p>
                <Link to="/cars-of-dara" className="mt-4 inline-block">
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white">{t('carsOfDara.addCar')}</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('carsOfDara.carColumn')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('sellCar.color')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('sellCar.year')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('sellCar.chassisNo')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('sellCar.plateNumber')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('carsOfDara.dateOfRegistration')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('carsOfDara.carOf')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('carsOfDara.soldTo')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('carsOfDara.proxiedTo')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm text-right w-[100px]">{t('settings.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tableLoading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={`skeleton-${i}`}>
                            {Array.from({ length: 10 }).map((_, j) => (
                              <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        cars.map((car) => (
                          <TableRow key={car.id} className="hover:bg-slate-50">
                            <TableCell className="text-sm text-slate-700 font-medium">{car.brand} {car.model}</TableCell>
                            <TableCell className="text-sm text-slate-700">{car.color}</TableCell>
                            <TableCell className="text-sm text-slate-700 tabular-nums">{car.year}</TableCell>
                            <TableCell className="text-sm text-slate-700">{car.chassisNo ?? '-'}</TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div><div>{car.plateNumber ?? '-'}</div>{car.plateStatus && <div className="text-slate-500 text-xs">{formatPlateStatus(car.plateStatus)}</div>}</div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500 tabular-nums">
                              {car.dateOfRegistration
                                ? new Date(car.dateOfRegistration).toLocaleDateString()
                                : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">{car.carOf ?? 'Dara'}</TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div><div>{car.soldTo ?? '-'}</div>{car.buyerPhoneNo && <div className="text-slate-500 text-xs">{car.buyerPhoneNo}</div>}</div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div><div>{car.proxiedTo ?? '-'}</div>{car.proxyPhoneNo && <div className="text-slate-500 text-xs">{car.proxyPhoneNo}</div>}</div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-slate-200 text-slate-700 hover:bg-slate-50"
                                  onClick={() => navigate(`/cars-of-dara/${car.id}/edit`)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-red-200 text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t('carsOfDara.delete')}</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t('carsOfDara.confirmDelete')} ({car.brand} {car.model})
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t('sellCar.cancel')}</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(car.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        {t('carsOfDara.delete')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {pagination && pagination.totalPages > 1 && !tableLoading && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-slate-500">
                      {t('exchangeCarHistory.showing')} {(currentPage - 1) * pagination.limit + 1} {t('exchangeCarHistory.to')} {Math.min(currentPage * pagination.limit, pagination.totalCount)} {t('exchangeCarHistory.of')} {pagination.totalCount} {t('exchangeCarHistory.results')}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPrevPage}
                        disabled={!pagination.hasPrevPage}
                        className="border-slate-200 text-slate-700 hover:bg-slate-50"
                      >
                        {t('exchangeCarHistory.previous')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={!pagination.hasNextPage}
                        className="border-slate-200 text-slate-700 hover:bg-slate-50"
                      >
                        {t('exchangeCarHistory.next')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
